import type { QueryCancelRequest } from '../../../../types/queryExecution';

/**
 * Stop-button escalation, shared by the Query Editor (`useQueryExecution`) and
 * Table Explorer (`TableExplorerTab.cancelFetch`).
 *
 * The first Stop is a polite cancel. Pressing Stop again while that cancel is
 * still outstanding escalates to `force`, which stops WAITING for the server to
 * honour the break and returns the query now (Oracle fires its watchdog
 * immediately, destroying the client socket; the other adapters ignore the
 * flag). It does not end the database session: the server finishes its own
 * wait, and an autocommitted statement still commits — see
 * `Docs/OracleConnectionLease.md` §1 for the measurement.
 *
 * The escalation is keyed to the **execution id**, never to a persistent
 * "cancelling" flag. Both surfaces can replace an in-flight execution without
 * an idle render in between — Table Explorer's TanStack query key changes on a
 * page/filter/sort change and `useTableData` claims `currentExecutionIdRef` for
 * the new fetch, while `isCancelling` only clears on an `isFetching === false`
 * render. Deriving `force` from that flag made the FIRST Stop on the *new*
 * execution a force-stop, killing a session the user never tried to cancel.
 */
export type CancelEscalation = {
  /** Payload for `cancelQueryV2` — carries `force: true` only when escalating. */
  request: QueryCancelRequest;
  /** Whether this press is the escalation (for analytics and UI messaging). */
  force: boolean;
};

/**
 * Build the cancel payload for a Stop press.
 *
 * @param executionId             The execution the user is stopping right now.
 * @param lastCancelledExecutionId The execution the last Stop press targeted,
 *   or `null` when nothing is outstanding (no cancel yet, or the previous one
 *   settled). Callers keep this in a ref, set it to `executionId` on every
 *   press, and clear it when the execution settles.
 */
export function nextCancelRequest(
  executionId: string,
  lastCancelledExecutionId: string | null,
): CancelEscalation {
  const force = executionId === lastCancelledExecutionId;
  return {
    request: force ? { executionId, force: true } : { executionId },
    force,
  };
}
